'use client';

import React, { useEffect, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { Modal } from "./Modal";
import { useTeam, errText } from "../internals";
import { useToast } from "./Toaster";

export const RemoveMemberDialog = ({ open, member, members, onClose, onDone }) => {
  const { storage } = useTeam();
  const toast = useToast();

  const [count, setCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(true);
  const [reassignTo, setReassignTo] = useState(""); // "" = leave unassigned
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !member) return;
    setLoadingCount(true);
    setReassignTo("");
    storage
      .getOpenItemsCount(member.id)
      .then((r) => setCount(r.open_enquiries))
      .catch(() => setCount(0))
      .finally(() => setLoadingCount(false));
  }, [open, member, storage]);

  if (!member) return null;

  const others = (members || []).filter((m) => m.id !== member.id && m.status === "active");

  const remove = async () => {
    setBusy(true);
    try {
      const res = await storage.removeMember(member.id, reassignTo || null);
      const n = res?.reassigned_enquiries || 0;
      const msg =
        n > 0 && reassignTo
          ? `${member.name} removed. ${n} enquir${n === 1 ? "y" : "ies"} moved over.`
          : `${member.name} removed from the team.`;
      toast.show(msg, "success");
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.show(errText(e), "error");
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title="Remove from team" testId="remove-member-dialog">
      <div className="px-5 py-5">
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-red-100">
          <AlertTriangle size={20} className="text-red-600" />
        </div>
        <p className="text-sm text-zinc-700">
          Remove <span className="font-semibold">{member.name}</span> from the team? This will take away their
          access and free up a seat in your plan. This can't be undone.
        </p>

        {loadingCount ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-zinc-500">
            <Loader2 size={14} className="animate-spin" /> Checking their open enquiries…
          </div>
        ) : count > 0 ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
            <p className="text-sm font-medium text-amber-800">
              {member.name} has {count} open enquir{count === 1 ? "y" : "ies"}.
            </p>
            <p className="mt-0.5 text-xs text-amber-700">What should happen to them?</p>
            <select
              data-testid="reassign-select"
              value={reassignTo}
              onChange={(e) => setReassignTo(e.target.value)}
              className="mt-2 w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500"
            >
              <option value="">Leave them unassigned</option>
              {others.map((m) => (
                <option key={m.id} value={m.id}>
                  Hand over to {m.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="mt-3 text-xs text-zinc-400">No open enquiries to hand over.</p>
        )}
      </div>

      <div className="flex gap-2 border-t border-zinc-200 px-5 py-4">
        <button
          type="button"
          data-testid="remove-cancel-btn"
          onClick={() => !busy && onClose()}
          disabled={busy}
          className="flex-1 rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="remove-confirm-btn"
          onClick={remove}
          disabled={busy || loadingCount}
          className="flex flex-1 items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
        >
          {busy && <Loader2 size={15} className="animate-spin" />}
          Remove from team
        </button>
      </div>
    </Modal>
  );
};
